import BaseAsr from './baseAsr';
import BaseTra from './baseTra';
import AzureAsr from './azure/asr';
import AzureTra from './azure/tra';
import MimiAsr from './mimi/asr';
import MimiTra from './mimi/tra';
import MimiAutoRefreshToken from './mimi/autoRefreshToken';
import AzureAutoRefreshToken from './azure/autoRefreshToken';
import BaseLid from './baseLid';
import AzureLid from './azure/lid';
import EventEmitter from 'events';
import debounce from 'lodash/debounce';

export default class SpeechRecognizer extends EventEmitter {
    _asrType;
    _traType;

    _isStop = true;

    /**
     * @type {BaseAsr|undefined}
     */
    _asr;

    /**
     * @type {BaseTra|undefined}
     */
    _tra;

     /**
     * @type {BaseLid|undefined}
     */
     _lid;

    /**
     * @type {MimiAutoRefreshToken}
     */
    _mimiAutoRefreshToken;

    /**
     * @type {AzureAutoRefreshToken}
     */
    _azureAutoRefreshToken;

    _sourceLang;
    _targetLang;

    /**
     * 服務常數對應的語言字串
     * @type {Object}
     */
    _serviceLangMap;

    /**
     * 服務常數對應的服務字串
     * @type {Object}
     */
    _serviceMap;

    _options = {
        lid: {
            use: false,
            recognizing: {
                // 是否交換語言, asr/tra重新初始化
                exchange: true,
            },
            recognized: {
                  // 是否交換語言, asr/tra重新初始化
                exchange: false,
            }
        }
    };

    constructor(){
        super();

        this._mimiAutoRefreshToken = new MimiAutoRefreshToken();
        this._azureAutoRefreshToken = new AzureAutoRefreshToken();

        this._mimiAutoRefreshToken.on('refresh', (() => {
            if(!this._isStop && (this._asrType == 2 || this._traType == 2)){
                this.start();
            }
        }).bind(this));

        this._azureAutoRefreshToken.on('refresh', (() => {
            if(!this._isStop && (this._asrType == 1 || this._traType == 1 || this._options.lid.use)){
                this.start();
            }
        }).bind(this));
    }

    serviceMap(serviceMap){
        this._serviceMap = serviceMap;
        return this;
    }

    serviceLangMap(serviceLangMap){
        this._serviceLangMap = serviceLangMap;
        return this;
    }

    sourceLang(sourceLang){
        this._sourceLang = sourceLang;
        return this;
    }

    targetLang(targetLang){
        this._targetLang = targetLang;
        return this;
    }

    asr(asrType){
        this._asrType = asrType;
        return this;
    }

    tra(traType){
        this._traType = traType;
        return this;
    }

    register(cb){
        this._registerCb = cb;
        return this;
    }

    async start(){
        this.stop();
        this._isStop = false;

        this._asr = await this.createAsr(this._asrType, this._sourceLang);
        this._tra = await this.createTra(this._traType, this._sourceLang, this._targetLang);
        this._asr.start();

        if(this._options.lid.use){
           this.setUpLid();
        }
    }

    stop(){
        this._asr?.stop();
        this._lid?.stop();
        this._isStop = true;
    }

    openLid(){
        this.closeLid();
        this._options.lid.use = true;
        if(!this._isStop){
            this.setUpLid();
        }
    }

    closeLid(){
        this._options.lid.use = false;
        this._lid?.stop();
    }

    async setUpLid(){
        const azureSourceLang = this._serviceLangMap[1][this._sourceLang];
        const azureTargetLang = this._serviceLangMap[1][this._targetLang];
        this._lid = await this.createLid();
        this._lid.start([azureSourceLang, azureTargetLang]);
    }

    async createLid(){
        const lid = AzureLid.fromToken(await this._azureAutoRefreshToken.token());

        const shouldSwap = (lang) => {
            const azureSourceLang = this._serviceLangMap[1][this._sourceLang];

            return lang !== azureSourceLang
        }

        const swapLangAndRestart = async () => {
            // swap
            [this._sourceLang, this._targetLang] = [this._targetLang, this._sourceLang];
            // 停掉原本的asr
            this._asr?.stop();
            this._asr = await this.createAsr(this._asrType, this._sourceLang);
            this._tra = await this.createTra(this._traType, this._sourceLang, this._targetLang);
            this._asr.start();
        }

        lid.on('recognizing', debounce(async (lang) => {
            if(!shouldSwap(lang)){
                return;
            }

            if(this._options.lid.recognizing.exchange){
                await swapLangAndRestart();
            }

            this.emit('lid-recognizing-lang-exchange');
        }, 1000));

        lid.on('recognized', async (lang) => {
            if(!shouldSwap(lang)){
                return
            }

            if(this._options.lid.recognized.exchange){
                await swapLangAndRestart();
            }

            this.emit('lid-recognized-lang-exchange');
        });

        return lid;
    }

    async createAsr(type, sourceLang){
        sourceLang = this._serviceLangMap[type][sourceLang];

        const asr = await (async() => {
            switch(parseInt(type)){
                // azure
                case 1:
                    return AzureAsr.fromToken(await this._azureAutoRefreshToken.token(), sourceLang);
                // mimi
                case 2:
                    return MimiAsr.fromToken(await this._mimiAutoRefreshToken.token(), sourceLang);
                default:
                    throw new Error('不合法的asr類型')
            }
        })();

        asr.on('recognizing', (text) => this.emit('asr-recognizing', text));
        asr.on('recognized', async (text) => this.emit('asr-recognized', text, await this._tra?.translate(text)));

        return asr;
    }

    async createTra(type, sourceLang, targetLang){
        sourceLang = this._serviceLangMap[type][this._sourceLang];
        targetLang = this._serviceLangMap[type][this._targetLang];

        switch(parseInt(type)){
            // azure
            case 1:
                return AzureTra.fromToken(await this._azureAutoRefreshToken.token(), sourceLang, targetLang);
            // mimi
            case 2:
                return MimiTra.fromToken(await this._mimiAutoRefreshToken.token(), sourceLang, targetLang);
            default:
                throw new Error('不合法的tra類型')
        }
    }

    when(condition, truthyHandle = () => {}, falsyHandle = () => {}){
        if(condition){
            truthyHandle(this);
        }else{
            falsyHandle(this);
        }

        return this;
    }
}
